import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box, Typography } from "@mui/material";
import EasyAnchorLink from "../../UI/AnchorLink";

import { GridColDef } from "@mui/x-data-grid-premium";
import { renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import KuebikoQueryCell from "../UI/KuebikoQueryCell";

import "../BrandMetadataReview.css";

interface NonOmniReviewProps {
  chain_id: string;
  country_code: string;
}

const ReviewNonOmniWebsites: React.FC<NonOmniReviewProps> = ({
  chain_id,
  country_code,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();

  const columns_def: GridColDef[] = [
    {
      field: "metadata_website",
      headerName: "Metadata Website",
      description:
        "The website domain associated with the brand as found in metadata.",
      type: "string",
      width: 250,
    },
    {
      field: "actual_website",
      headerName: "Non-Omni Website",
      description:
        "The website domain associated with the brand as found in the places table.",
      type: "string",
      width: 250,
    },
    {
      field: "actual_brand_website_count",
      headerName: "Brand Website Count",
      description:
        "The count of occurrences of the website domain in the places table for places from non-omni sources.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "kuebiko_query",
      headerName: "Kuebiko Query",
      description:
        "Copy or view the Kuebiko query used to review places for retraction.",
      filterable: false,
      sortable: false,
      width: 250,
      renderCell: (params) => {
        const queryTemplate = `
        SELECT id, name, country_code, business_category_id, address, chain_id, website,
            external_ids -> 'EXTERNAL_ID' AS "omni_id"
        FROM "places"
        WHERE external_ids -> 'EXTERNAL_ID' IS NULL
        AND country_code = 'COUNTRY_CODE'
        AND website LIKE '%ACTUAL_WEBSITE%'
        `;

        const columnMappings = {
          EXTERNAL_ID: params.row.external_id,
          COUNTRY_CODE: params.row.country_code,
          ACTUAL_WEBSITE: params.row.actual_website,
        };

        return (
          <KuebikoQueryCell
            queryTemplate={queryTemplate}
            columnMappings={columnMappings}
            tooltip="Kuebiko Query"
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (chain_id && country_code) {
      setLoading(true);
      const params = new URLSearchParams({
        chain_id: chain_id,
        country_code: country_code,
      });

      const fetchBusinessWebsite = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_metadata/non_omni_website_review?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch places business websites to review for chain_id ${chain_id} + ${country_code}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchBusinessWebsite();
    } else {
      setData([]);
    }
  }, [chain_id, country_code]);

  return (
    <Box className="metadata-review-container">
      <Box display="flex" alignItems="center" id="business-websites">
        <EasyAnchorLink id="business-websites" />
        <Typography variant="h6" gutterBottom sx={{ fontSize: "1.25rem" }}>
          Business Websites
        </Typography>
      </Box>

      <GaneshDataGrid
        gridId="nonOmniReviewWebsites"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data.length > 0 ? true : false}
        customHeight={data.length > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewNonOmniWebsites;
